/** Courier accounts, consignments and payouts (FR-CRR-01..03). */
import { z } from "zod";
import { idSchema, poishaSchema } from "./inventory";

export const COURIER_PROVIDERS = ["pathao", "steadfast", "redx"] as const;
export type CourierProviderId = (typeof COURIER_PROVIDERS)[number];

const secret = z.string().trim().min(4).max(500);
const label = z.string().trim().min(2).max(60);

export const courierConnectSchema = z.discriminatedUnion("provider", [
  z.object({
    provider: z.literal("pathao"),
    label,
    credentials: z.object({
      clientId: secret,
      clientSecret: secret,
      username: z.email(),
      password: z.string().min(4).max(200),
      sandbox: z.boolean().default(false),
    }),
  }),
  z.object({
    provider: z.literal("steadfast"),
    label,
    credentials: z.object({ apiKey: secret, secretKey: secret }),
  }),
  z.object({
    provider: z.literal("redx"),
    label,
    credentials: z.object({ accessToken: secret, sandbox: z.boolean().default(false) }),
  }),
]);
export type CourierConnect = z.output<typeof courierConnectSchema>;

export const courierAccountUpdateSchema = z
  .object({
    label: label.optional(),
    /** Pathao store or RedX pickup store parcels leave from. */
    storeId: z.string().trim().max(40).nullable().optional(),
    status: z.enum(["active", "disabled"]).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, "Nothing to update");

export const shipmentBookSchema = z.object({
  courierAccountId: idSchema,
  /** Cash the courier collects; defaults to what the order still owes. */
  cod: poishaSchema.optional(),
  weightGrams: z.number().int().min(50).max(30_000).default(500),
  note: z.string().trim().max(200).default(""),
  /** Courier-specific destination: Pathao city/zone, RedX area. */
  area: z
    .object({
      cityId: z.string().max(20).optional(),
      zoneId: z.string().max(20).optional(),
      areaId: z.string().max(20).optional(),
      areaName: z.string().max(80).optional(),
    })
    .default({}),
  /** Where a prepaid order's money arrived, as when shipping by hand. */
  payment: z
    .object({ accountId: idSchema, txnRef: z.string().trim().min(4).max(40).optional() })
    .optional(),
});
export type ShipmentBook = z.output<typeof shipmentBookSchema>;

export const courierPayoutSchema = z.object({
  courierAccountId: idSchema,
  shipmentIds: z.array(idSchema).min(1).max(500),
  /** What actually arrived; the difference from the cash collected is the courier's charge. */
  received: poishaSchema.refine((v) => v > 0, "Enter the amount you received"),
  toAccountId: idSchema,
  reference: z.string().trim().min(4).max(60).optional(),
});
export type CourierPayout = z.output<typeof courierPayoutSchema>;
